import { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  FileText,
  Download,
  ChevronLeft,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  HelpCircle,
} from "lucide-react";
import { API } from "@/lib/api";
import { ENTRY_KINDS, importableKindsForVehicle, importableKindsForFleet } from "@/lib/entryKinds";
import { useCsvParser } from "@/lib/csv/useCsvParser";
import { SCHEMA_BY_KIND } from "@/lib/csv/schemas";
import { autoMap, setMapping, findMissingRequirements } from "@/lib/csv/headerMapper";
import { validate } from "@/lib/csv/csvValidator";
import { sendRows } from "@/lib/csv/bulkSender";

/**
 * BulkImportDialog — client-side CSV mapping importer.
 *
 * Flow: Pick kind → Upload + parse (PapaParse) → Map columns
 * (alias auto-detect + user override) → Validate (per-row, no whole-file
 * fail) → Loop POST per row → Result summary.
 *
 * No backend changes: each row is sent through the existing single-record
 * endpoints (TripsAPI.create / EnergyAPI.create).
 */

const NULL_HEADER = "__none__"; // shadcn Select doesn't accept "" as a value

const confidenceColor = {
  auto: { dot: "bg-emerald-400", text: "text-emerald-300", border: "border-emerald-900/40", label: "Auto" },
  guess: { dot: "bg-amber-400", text: "text-amber-200", border: "border-amber-900/40", label: "Guess" },
  manual: { dot: "bg-sky-400", text: "text-sky-200", border: "border-sky-900/40", label: "Manual" },
  derived: { dot: "bg-violet-400", text: "text-violet-200", border: "border-violet-900/40", label: "Derived" },
  unmapped: { dot: "bg-[#3a3a3a]", text: "text-[var(--text-muted)]", border: "border-[var(--border-subtle)]", label: "—" },
};

export default function BulkImportDialog({
  open,
  onOpenChange,
  vehicles,
  defaultVehicleId,
  defaultKind = null,
  unit,
  onImported,
}) {
  const [kind, setKind] = useState(defaultKind);
  const [vehicleId, setVehicleId] = useState(defaultVehicleId || "");
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("configure"); // configure | map | result
  const [mapping, setMappingState] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState(null); // ValidationResult
  const [result, setResult] = useState(null); // { inserted, errors, skipped }
  const [fatalError, setFatalError] = useState(null);
  const [errorsExpanded, setErrorsExpanded] = useState(true);
  const fileRef = useRef(null);
  const parser = useCsvParser();

  const resetAll = () => {
    setKind(defaultKind);
    setVehicleId(defaultVehicleId || vehicles[0]?.id || "");
    setFile(null);
    setStep("configure");
    setMappingState({});
    setValidation(null);
    setResult(null);
    setFatalError(null);
    setErrorsExpanded(true);
    parser.reset();
  };

  useEffect(() => {
    if (open) resetAll();
  }, [open, defaultKind, defaultVehicleId, vehicles, unit]);

  const selectedVehicle = useMemo(() => vehicles.find((v) => v.id === vehicleId), [vehicles, vehicleId]);
  const availableKinds = useMemo(() => {
    if (selectedVehicle) return importableKindsForVehicle(selectedVehicle);
    return importableKindsForFleet(vehicles);
  }, [selectedVehicle, vehicles]);
  useEffect(() => {
    if (kind && !availableKinds.includes(kind)) setKind(null);
  }, [kind, availableKinds]);

  const schema = kind ? SCHEMA_BY_KIND[kind] : null;
  const kindMeta = kind ? ENTRY_KINDS[kind === "trip" ? "trip" : kind] : null;

  const templateUrl = useMemo(() => {
    if (kind === "trip") return `${API}/trips/csv-template`;
    if (kind === "charging") return `${API}/energy-events/csv-template?type=charge`;
    if (kind === "fuel") return `${API}/energy-events/csv-template?type=fuel`;
    return null;
  }, [kind]);

  // -------- step transitions --------
  const goToMapping = async () => {
    if (!file || !schema) return;
    setFatalError(null);
    const parsed = await parser.parseFile(file);
    if (parsed.fatalErrors.length) {
      setFatalError(parsed.fatalErrors[0]);
      return;
    }
    if (!parsed.headers.length || !parsed.rows.length) {
      setFatalError("That file looks empty — make sure the first row contains column headers.");
      return;
    }
    setMappingState(autoMap(schema, parsed.headers));
    setStep("map");
  };

  const updateMapping = (fieldKey, header) => {
    if (!schema) return;
    setMappingState((cur) => setMapping(schema, cur, fieldKey, header === NULL_HEADER ? null : header));
  };

  const missingRequired = useMemo(() => {
    if (!schema) return [];
    return findMissingRequirements(schema, mapping);
  }, [schema, mapping]);

  const runImport = async () => {
    if (!schema || !parser.result || missingRequired.length) return;
    setSubmitting(true);
    setFatalError(null);
    try {
      const v = validate({ schema, parsed: parser.result, mapping });
      setValidation(v);
      // Loop POST for the valid rows.
      const outcome = await sendRows({ kind, vehicleId, rows: v.validRows });
      const localSkipped = v.invalidRows.map((r) => ({ rowNumber: r.rowNumber, reason: r.errors.join("; ") }));
      const combined = {
        inserted: outcome.inserted,
        errors: [...localSkipped, ...outcome.errors].sort((a, b) => a.rowNumber - b.rowNumber),
      };
      onImported?.({ kind, ...combined });
      setResult(combined);
      setStep("result");
    } catch (e) {
      setFatalError(e?.message || "Unexpected error during import.");
    } finally {
      setSubmitting(false);
    }
  };

  const importAnother = () => {
    setFile(null);
    setStep("configure");
    setMappingState({});
    setValidation(null);
    setResult(null);
    setFatalError(null);
    setErrorsExpanded(true);
    parser.reset();
  };

  const headerOptions = parser.result?.headers || [];
  const skippedCount = result?.errors?.length || 0;

  // -------- render --------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] sm:max-w-[640px] p-0 overflow-hidden"
        data-testid="bulk-import-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <div className="flex items-center gap-2 mb-1">
            {((kind && step === "configure") || step === "map") && (
              <button
                type="button"
                onClick={() => (step === "map" ? setStep("configure") : setKind(null))}
                className="tap w-7 h-7 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] -ml-1"
                data-testid="bulk-import-back-btn"
                aria-label="Back"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={1.75} />
              </button>
            )}
            <DialogTitle className="font-display text-2xl font-light tracking-tight">
              {step === "result"
                ? skippedCount === 0
                  ? "Import complete"
                  : "Import finished with skipped rows"
                : step === "map"
                ? "Map your columns"
                : kind
                ? `Import ${kindMeta?.label}`
                : "Bulk Import"}
            </DialogTitle>
          </div>
          <DialogDescription className="text-[var(--text-muted)] text-sm leading-relaxed">
            {step === "result"
              ? skippedCount === 0
                ? "Every row in your file was imported."
                : "Some rows were skipped. Review the reasons below and fix only those rows in your source file."
              : step === "map"
              ? "We auto-detected your columns. Review and adjust below — any column can be re-assigned."
              : kind
              ? "CSV or Excel (.xlsx). Use any export format — we'll map the columns next."
              : "What kind of records do you want to bring in?"}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: kind picker */}
        {!kind && (
          <div className="px-6 pb-6 grid grid-cols-1 gap-2.5" data-testid="bulk-import-kind-grid">
            {availableKinds.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)] py-6 text-center border border-dashed border-[var(--border-subtle)]" style={{ borderRadius: 2 }}>
                No vehicles available yet. Add a vehicle first.
              </div>
            ) : (
              availableKinds.map((k) => {
                const meta = ENTRY_KINDS[k];
                const Icon = meta.icon;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKind(k)}
                    data-testid={`bulk-import-kind-${k}`}
                    className="group flex items-center gap-3 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] hover:border-white/30 px-4 py-3.5 text-left transition-colors"
                    style={{ borderRadius: 4 }}
                  >
                    <span className="w-9 h-9 flex items-center justify-center bg-[var(--bg-emphasis)] border border-[var(--border-subtle)]" style={{ borderRadius: 999 }}>
                      <Icon className="w-4 h-4 text-[var(--text-primary)]" strokeWidth={1.75} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[var(--text-primary)] tracking-tight">{meta.label}</div>
                      <div className="text-[12px] text-[var(--text-secondary)] truncate">{meta.description}</div>
                    </div>
                    <span className="text-[var(--text-faint)] group-hover:text-[var(--text-primary)] text-sm transition-colors">→</span>
                  </button>
                );
              })
            )}
          </div>
        )}

        {/* Step 2: configure + upload */}
        {kind && step === "configure" && (
          <>
            <div className="px-6 pb-2 space-y-4">
              <div className="space-y-2">
                <Label className="label-tiny">Target Vehicle</Label>
                <Select value={vehicleId} onValueChange={setVehicleId}>
                  <SelectTrigger className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]" data-testid="bulk-import-vehicle-select">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]">
                    {vehicles
                      .filter((v) => importableKindsForVehicle(v).includes(kind))
                      .map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                          {v.energy_type && <span className="text-[var(--text-muted)] ml-2 text-xs">· {v.energy_type}</span>}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="label-tiny">{kindMeta?.label} File</Label>
                  {templateUrl && (
                    <a
                      href={templateUrl}
                      download
                      className="inline-flex items-center gap-1 text-[11px] tracking-wider text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                      data-testid="bulk-import-template-link"
                    >
                      <Download className="w-3 h-3" strokeWidth={1.75} />
                      Download template
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full border border-dashed border-[var(--border-subtle)] hover:border-[var(--border-strong)] py-7 flex flex-col items-center gap-2 transition-colors bg-[var(--bg-input)]"
                  style={{ borderRadius: 4 }}
                  data-testid="bulk-import-dropzone"
                >
                  {file ? (
                    <>
                      <FileText className="w-6 h-6 text-[var(--text-primary)]" strokeWidth={1.5} />
                      <span className="text-sm text-[var(--text-primary)] font-mono-data">{file.name}</span>
                      <span className="text-xs text-[var(--text-muted)]">{(file.size / 1024).toFixed(1)} KB</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-[var(--text-muted)]" strokeWidth={1.5} />
                      <span className="text-sm text-[var(--text-primary)]">Click to select file</span>
                      <span className="text-xs text-[var(--text-muted)]">.csv, .xlsx, .xls — any column layout</span>
                    </>
                  )}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="bulk-import-file-input"
                />
              </div>

              {fatalError && (
                <div
                  className="flex items-start gap-2 px-3 py-2.5 border border-[var(--border-danger-soft)] bg-[var(--bg-danger-soft)] text-[#fca5a5] text-xs leading-relaxed"
                  style={{ borderRadius: 4 }}
                  data-testid="bulk-import-fatal-error"
                >
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                  <span>{fatalError}</span>
                </div>
              )}
            </div>

            <DialogFooter className="px-6 pb-6 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                data-testid="bulk-import-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={goToMapping}
                disabled={parser.parsing || !vehicleId || !file}
                className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
                data-testid="bulk-import-next-btn"
              >
                {parser.parsing ? "Reading..." : "Next: Map columns"}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 3: column mapping */}
        {kind && step === "map" && schema && (
          <>
            <div className="px-6 pb-2 space-y-3" data-testid="bulk-import-mapping">
              <div className="flex items-center gap-2 text-[11px] text-[var(--text-secondary)] tracking-wider">
                <Sparkles className="w-3 h-3 text-emerald-400" strokeWidth={2} />
                <span>
                  {parser.result?.rows.length || 0} rows · {parser.result?.headers.length || 0} columns detected
                </span>
              </div>
              <div
                className="border border-[var(--border-subtle)] bg-[var(--bg-elevated)] divide-y divide-[var(--border-subtle)] max-h-[340px] overflow-y-auto"
                style={{ borderRadius: 4 }}
                data-testid="bulk-import-mapping-list"
              >
                {schema.fields.map((fs) => {
                  const m = mapping[fs.key] || { confidence: "unmapped", header: null };
                  const c = confidenceColor[m.confidence];
                  return (
                    <div
                      key={fs.key}
                      className="grid grid-cols-[1fr_240px] gap-3 items-center px-4 py-2.5"
                      data-testid={`bulk-import-field-row-${fs.key}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} aria-hidden />
                          <span className="text-sm text-[var(--text-primary)] tracking-tight">
                            {fs.label}
                            {fs.required && <span className="text-[#E31937] ml-1">*</span>}
                          </span>
                          <span className={`text-[10px] tracking-[0.14em] uppercase ${c.text}`} data-testid={`bulk-import-field-confidence-${fs.key}`}>
                            {c.label}
                          </span>
                        </div>
                        {m.confidence === "derived" && fs.derive?.description && (
                          <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 ml-3.5 flex items-center gap-1">
                            <HelpCircle className="w-3 h-3" strokeWidth={1.75} />
                            {fs.derive.description}
                          </div>
                        )}
                      </div>
                      <Select
                        value={m.header || NULL_HEADER}
                        onValueChange={(val) => updateMapping(fs.key, val)}
                        disabled={m.confidence === "derived"}
                      >
                        <SelectTrigger
                          className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] h-9 text-xs"
                          data-testid={`bulk-import-field-select-${fs.key}`}
                        >
                          <SelectValue placeholder={m.confidence === "derived" ? "(auto-calculated)" : "Not mapped"} />
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]">
                          <SelectItem value={NULL_HEADER}>
                            <span className="text-[var(--text-muted)]">Not mapped</span>
                          </SelectItem>
                          {headerOptions.map((h) => (
                            <SelectItem key={h} value={h}>
                              {h}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>

              {missingRequired.length > 0 && (
                <div
                  className="flex items-start gap-2 px-3 py-2.5 border border-[var(--border-danger-soft)] bg-[var(--bg-danger-soft)] text-[#fca5a5] text-xs leading-relaxed"
                  style={{ borderRadius: 4 }}
                  data-testid="bulk-import-missing-required"
                >
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                  <span>Required: {missingRequired.join(", ")}</span>
                </div>
              )}
              {fatalError && (
                <div
                  className="flex items-start gap-2 px-3 py-2.5 border border-[var(--border-danger-soft)] bg-[var(--bg-danger-soft)] text-[#fca5a5] text-xs leading-relaxed"
                  style={{ borderRadius: 4 }}
                  data-testid="bulk-import-fatal-error"
                >
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                  <span>{fatalError}</span>
                </div>
              )}
            </div>

            <DialogFooter className="px-6 pb-6 pt-2">
              <Button
                variant="outline"
                onClick={() => setStep("configure")}
                className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                data-testid="bulk-import-map-back-btn"
              >
                Back
              </Button>
              <Button
                onClick={runImport}
                disabled={submitting || missingRequired.length > 0}
                className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
                data-testid="bulk-import-submit-btn"
              >
                {submitting ? "Importing..." : `Import ${parser.result?.rows.length || 0} rows`}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 4: result */}
        {step === "result" && result && (
          <>
            <div className="px-6 pb-2 space-y-4" data-testid="bulk-import-result">
              <div className="grid grid-cols-2 gap-2.5">
                <div className="bg-[var(--bg-elevated)] border border-[var(--border-subtle)] px-4 py-3.5" style={{ borderRadius: 4 }} data-testid="bulk-import-inserted-count">
                  <div className="flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-[var(--text-secondary)]">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" strokeWidth={2} />
                    Imported
                  </div>
                  <div className="mt-1 font-mono-data text-2xl font-light tracking-tight text-[var(--text-primary)] tabular-nums">{result.inserted}</div>
                </div>
                <div
                  className="bg-[var(--bg-elevated)] border px-4 py-3.5"
                  style={{ borderRadius: 4, borderColor: skippedCount > 0 ? "#3a1f08" : "#1f1f1f" }}
                  data-testid="bulk-import-skipped-count"
                >
                  <div className="flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-[var(--text-secondary)]">
                    <AlertTriangle className={`w-3 h-3 ${skippedCount > 0 ? "text-amber-400" : "text-[var(--text-faint)]"}`} strokeWidth={2} />
                    Skipped
                  </div>
                  <div className="mt-1 font-mono-data text-2xl font-light tracking-tight text-[var(--text-primary)] tabular-nums">{skippedCount}</div>
                </div>
              </div>

              {skippedCount > 0 && (
                <div className="border border-[var(--border-subtle)] bg-[var(--bg-elevated)]" style={{ borderRadius: 4 }} data-testid="bulk-import-errors-panel">
                  <button
                    type="button"
                    onClick={() => setErrorsExpanded((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#101010] transition-colors"
                    data-testid="bulk-import-errors-toggle"
                    aria-expanded={errorsExpanded}
                  >
                    <span className="text-[11px] tracking-[0.14em] uppercase text-[var(--text-secondary)]">Skipped rows ({skippedCount})</span>
                    {errorsExpanded ? (
                      <ChevronUp className="w-3.5 h-3.5 text-[var(--text-muted)]" strokeWidth={1.75} />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 text-[var(--text-muted)]" strokeWidth={1.75} />
                    )}
                  </button>
                  {errorsExpanded && (
                    <ul className="max-h-56 overflow-y-auto border-t border-[var(--border-subtle)] divide-y divide-[var(--border-subtle)]" data-testid="bulk-import-errors-list">
                      {result.errors.map((err, idx) => (
                        <li
                          key={`${err.rowNumber}-${idx}`}
                          className="px-4 py-2.5 flex items-start gap-3 text-xs leading-relaxed"
                          data-testid="bulk-import-error-row"
                        >
                          <span className="font-mono-data text-[10px] text-[var(--text-secondary)] tracking-wider pt-0.5 min-w-[42px]">Row {err.rowNumber}</span>
                          <span className="text-[var(--text-primary)]/85">{err.reason}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {skippedCount === 0 && result.inserted === 0 && (
                <div className="text-xs text-[var(--text-muted)] leading-relaxed border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-4 py-3" style={{ borderRadius: 4 }} data-testid="bulk-import-empty-note">
                  No rows were imported. Your file may be empty or contain only zero-distance entries.
                </div>
              )}
            </div>

            <DialogFooter className="px-6 pb-6 pt-2">
              <Button
                variant="outline"
                onClick={importAnother}
                className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                data-testid="bulk-import-another-btn"
              >
                Import another
              </Button>
              <Button onClick={() => onOpenChange(false)} className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]" data-testid="bulk-import-done-btn">
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
